/**
 * Bottom sheet with in-call controls:
 * - Mute / Unmute toggle
 * - Camera On / Off toggle
 */

import React, { useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { StyleSheet, Switch, View, useWindowDimensions } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { Icon } from 'react-native-paper'
import { isNumber } from 'lodash'
import { DS } from '../../design-system'
import { isWeb, useMaxWidth } from '../WebWrapper'

export type AVControlsSheetRef = {
  open: () => void
  close: () => void
}

type Props = {
  isMuted: boolean
  isCameraOn: boolean
  onToggleMute: (muted: boolean) => void
  onToggleCamera: (cameraOn: boolean) => void
  /** Called when the sheet is dismissed (backdrop tap, swipe, or programmatic). */
  onDismiss?: () => void
}

export const AVControlsSheet = forwardRef<AVControlsSheetRef, Props>(
  ({ isMuted, isCameraOn, onToggleMute, onToggleCamera, onDismiss }, ref) => {
    const bottomSheetRef = useRef<BottomSheetModal>(null)
    const { width: screenWidth } = useWindowDimensions()
    const maxWidth = useMaxWidth()
    const _maxWidth = isNumber(maxWidth) ? maxWidth : screenWidth
    const marginHorizontal = screenWidth > _maxWidth ? (screenWidth - _maxWidth) / 2 : 0

    useImperativeHandle(ref, () => ({
      open: () => bottomSheetRef.current?.present(),
      close: () => bottomSheetRef.current?.dismiss()
    }))

    const renderBackdrop = useCallback(
      (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
        <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} />
      ),
      []
    )

    return (
      <BottomSheetModal
        ref={bottomSheetRef}
        enableDynamicSizing
        backgroundStyle={styles.background}
        handleIndicatorStyle={styles.handle}
        backdropComponent={renderBackdrop}
        onDismiss={onDismiss}
        containerStyle={{
          marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined
        }}
        style={{
          marginHorizontal: 'auto',
          maxWidth: maxWidth
        }}
      >
        <BottomSheetView style={styles.content}>
          <DS.Text variant="Heading 3/Regular" textAlign="center">
            Call Controls
          </DS.Text>

          <View style={styles.controlRow}>
            <View style={styles.controlLabel}>
              <View style={styles.iconCircle}>
                <Icon source={isMuted ? 'microphone-off' : 'microphone'} size={24} color="#000" />
              </View>
              <View>
                <DS.Text variant="Heading 4/Regular">Microphone</DS.Text>
                <DS.Text variant="Mini/Bold">{isMuted ? 'Off' : 'On'}</DS.Text>
              </View>
            </View>
            <Switch
              trackColor={{ false: '#DDDAD2', true: '#7B043D' }}
              thumbColor={!isMuted ? '#fff' : '#f4f3f4'}
              ios_backgroundColor="#DDDAD2"
              value={!isMuted}
              onValueChange={(on) => onToggleMute(!on)}
            />
          </View>

          <View style={styles.controlRow}>
            <View style={styles.controlLabel}>
              <View style={styles.iconCircle}>
                <Icon source={isCameraOn ? 'video' : 'video-off'} size={24} color="#000" />
              </View>
              <View>
                <DS.Text variant="Heading 4/Regular">Camera</DS.Text>
                <DS.Text variant="Mini/Bold">{isCameraOn ? 'On' : 'Off'}</DS.Text>
              </View>
            </View>
            <Switch
              trackColor={{ false: '#DDDAD2', true: '#7B043D' }}
              thumbColor={isCameraOn ? '#fff' : '#f4f3f4'}
              ios_backgroundColor="#DDDAD2"
              value={isCameraOn}
              onValueChange={(on) => onToggleCamera(on)}
            />
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    )
  }
)

AVControlsSheet.displayName = 'AVControlsSheet'

const styles = StyleSheet.create({
  background: {
    backgroundColor: DS.palette['Brand/Cream']
  },
  handle: {
    backgroundColor: '#0003'
  },
  content: {
    paddingHorizontal: DS.spacing['16'],
    paddingTop: DS.spacing['8'],
    paddingBottom: DS.spacing['32'],
    gap: DS.spacing['16'],
    alignItems: 'center'
  },
  controlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  controlLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#DDDAD2'
  }
})
